import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Card from '../../../molecules/Card';
import TimeSeriesChart from './TimeSeriesChart';
import TimelineToolbar from './TimelineToolbar';
import StatusTimeline from './StatusTimeline';
import { formatDuration, formatBytes, formatInteger, formatMilliseconds } from '../utils/formatters';
import { getStatusModifier, formatStatusLabel } from '../utils/status';

const ServiceDetailPanel = ({
    service = null,
    history = null,
    range,
    rangeOptions,
    onRangeChange,
    isLoading = false,
    error = null,
    onRetry = () => {},
}) => {
    const detail = useMemo(() => buildDetailState(history), [history]);
    const isServiceSelected = Boolean(service);
    const statusModifier = getStatusModifier(service?.status);
    const statusLabel = formatStatusLabel(service?.status);
    const selectedRange = rangeOptions.find((option) => option.value === range)?.label || '';

    const summaryMetrics = isServiceSelected ? buildSummaryMetrics(detail.samples) : [];
    const charts = isServiceSelected ? buildCharts(detail.samples) : [];

    const header = isServiceSelected ? (
        <div className="service-detail__header">
            <div className="service-detail__heading">
                <h2>{service.name || service.id}</h2>
                <p>Granular uptime, resource, and throughput metrics with Grafana-style visualisations.</p>
            </div>
            <div className="service-detail__controls">
                <div className="service-detail__status" aria-live="polite">
                    <span className={`service-detail__status-dot service-detail__status-dot--${statusModifier}`} aria-hidden="true" />
                    <span className="service-detail__status-label">{statusLabel}</span>
                </div>
                <TimelineToolbar options={rangeOptions} value={range} onChange={onRangeChange} disabled={isLoading} />
            </div>
        </div>
    ) : <EmptyHeader />;

    let bodyContent;
    if (!isServiceSelected) {
        bodyContent = (
            <div className="service-detail__placeholder">Select a service to inspect detailed telemetry.</div>
        );
    } else if (error) {
        bodyContent = (
            <div className="service-detail__error" role="alert">
                <p>{error}</p>
                <button type="button" className="btn-secondary" onClick={onRetry} disabled={isLoading}>
                    Retry loading history
                </button>
            </div>
        );
    } else {
        bodyContent = (
            <>
                <section className="service-detail__summary" aria-label="Service summary">
                    {summaryMetrics.map((metric) => (
                        <div key={metric.label} className="service-detail__summary-item">
                            <span className="service-detail__summary-label">{metric.label}</span>
                            <span className="service-detail__summary-value">{metric.value}</span>
                            {metric.helper && <span className="service-detail__summary-helper">{metric.helper}</span>}
                        </div>
                    ))}
                </section>
                <section className="service-detail__timeline" aria-label="Service status timeline">
                    <header className="service-detail__section-header">
                        <h3>Status timeline</h3>
                        <span className="service-detail__section-caption">{selectedRange ? `Last ${selectedRange}` : 'Recent window'}</span>
                    </header>
                    <StatusTimeline samples={detail.statusSamples} />
                </section>
                <section className="service-detail__charts" aria-label="Service metrics charts">
                    {charts.map((chart) => (
                        <TimeSeriesChart
                            key={chart.key}
                            title={chart.title}
                            data={chart.data}
                            color={chart.color}
                            valueFormatter={chart.valueFormatter}
                            yAxisFormatter={chart.yAxisFormatter}
                            emptyLabel={chart.emptyLabel}
                        />
                    ))}
                </section>
            </>
        );
    }

    const footer = (
        <div className="service-detail__footer-note">
            {isServiceSelected ? (
                detail.rangeLabel ? (
                    <span>{detail.rangeLabel}</span>
                ) : (
                    <span>Historical metrics will populate as the health service polls actuator endpoints.</span>
                )
            ) : (
                <span>Historical metrics appear once a service is selected and data is recorded.</span>
            )}
            {isServiceSelected && isLoading && <span className="service-detail__loading" aria-live="polite">Updating metrics…</span>}
        </div>
    );

    return (
        <Card
            className="system-health-page__details"
            allowOverflow
            title={header}
            body={(
                <div className="service-detail__body">
                    {isServiceSelected && isLoading && (
                        <div className="service-detail__overlay" aria-live="polite">Refreshing history…</div>
                    )}
                    {bodyContent}
                </div>
            )}
            footer={footer}
        />
    );
};

ServiceDetailPanel.propTypes = {
    service: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        status: PropTypes.string,
    }),
    history: PropTypes.shape({
        samples: PropTypes.arrayOf(PropTypes.shape({
            timestamp: PropTypes.string,
            status: PropTypes.string,
            cpuUsage: PropTypes.number,
        })),
        firstSampleAt: PropTypes.string,
        lastSampleAt: PropTypes.string,
        totalSamples: PropTypes.number,
    }),
    range: PropTypes.string.isRequired,
    rangeOptions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })).isRequired,
    onRangeChange: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    onRetry: PropTypes.func,
};

const buildDetailState = (history) => {
    if (!history || !Array.isArray(history.samples)) {
        return {
            samples: [],
            statusSamples: [],
            rangeLabel: null,
        };
    }
    const samples = history.samples
        .map((sample) => {
            const timestamp = new Date(sample.timestamp);
            if (Number.isNaN(timestamp.getTime())) {
                return null;
            }
            const requestRatePerMinute = sample.requestRatePerSecond !== null && sample.requestRatePerSecond !== undefined
                ? sample.requestRatePerSecond * 60
                : null;
            const errorRatePerMinute = sample.errorRatePerSecond !== null && sample.errorRatePerSecond !== undefined
                ? sample.errorRatePerSecond * 60
                : null;
            const memoryPercent = sample.heapUsedBytes !== null && sample.heapUsedBytes !== undefined
                && sample.heapMaxBytes !== null && sample.heapMaxBytes !== undefined && sample.heapMaxBytes > 0
                ? (sample.heapUsedBytes / sample.heapMaxBytes) * 100
                : null;
            return {
                timestamp,
                status: sample.status,
                cpuPercent: sample.cpuUsage !== null && sample.cpuUsage !== undefined ? sample.cpuUsage * 100 : null,
                load1m: sample.systemLoad1m,
                memoryUsedBytes: sample.heapUsedBytes,
                memoryMaxBytes: sample.heapMaxBytes,
                memoryPercent,
                requestRatePerMinute,
                errorRatePerMinute,
                latencyMs: sample.averageResponseMs,
                uptimeSeconds: sample.uptimeSeconds,
            };
        })
        .filter(Boolean);

    const statusSamples = samples.map((sample) => ({ timestamp: sample.timestamp, status: sample.status }));

    let rangeLabel = null;
    if (history.firstSampleAt && history.lastSampleAt) {
        const first = new Date(history.firstSampleAt);
        const last = new Date(history.lastSampleAt);
        if (!Number.isNaN(first.getTime()) && !Number.isNaN(last.getTime())) {
            const formatter = new Intl.DateTimeFormat(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
            rangeLabel = `Telemetry from ${formatter.format(first)} to ${formatter.format(last)} (${history.totalSamples ?? samples.length} samples)`;
        }
    }

    return {
        samples,
        statusSamples,
        rangeLabel,
    };
};

const buildSummaryMetrics = (samples) => {
    if (!samples || samples.length === 0) {
        return [
            { label: 'Uptime', value: 'N/A' },
            { label: 'CPU', value: 'N/A' },
            { label: 'Requests/min', value: 'N/A' },
            { label: 'Errors/min', value: 'N/A' },
            { label: 'Heap', value: 'N/A' },
            { label: 'Latency', value: 'N/A' },
        ];
    }

    const latest = samples[samples.length - 1];

    const numeric = (arr) => arr.filter((v) => v !== null && v !== undefined && Number.isFinite(v));
    const avg = (arr) => {
        const values = numeric(arr);
        if (!values.length) return null;
        return values.reduce((a, b) => a + b, 0) / values.length;
    };

    const avgCpu = avg(samples.map((s) => s.cpuPercent));
    const avgReq = avg(samples.map((s) => s.requestRatePerMinute));
    const avgErr = avg(samples.map((s) => s.errorRatePerMinute));
    const avgLatency = avg(samples.map((s) => s.latencyMs));

    const heapUsage = latest.memoryPercent !== null && latest.memoryPercent !== undefined
        ? `${Math.round(latest.memoryPercent)}%`
        : latest.memoryUsedBytes !== null && latest.memoryUsedBytes !== undefined
            ? formatBytes(latest.memoryUsedBytes)
            : 'N/A';
    const heapHelper = latest.memoryUsedBytes !== null && latest.memoryMaxBytes
        ? `${formatBytes(latest.memoryUsedBytes)} of ${formatBytes(latest.memoryMaxBytes)}`
        : null;

    return [
        { label: 'Uptime', value: formatDuration(latest.uptimeSeconds) || 'N/A' },
        { label: 'CPU', value: avgCpu !== null ? `${Math.round(avgCpu)}%` : 'N/A' },
        { label: 'Requests/min', value: avgReq !== null ? formatInteger(avgReq) : 'N/A' },
        { label: 'Errors/min', value: avgErr !== null ? formatInteger(avgErr) : 'N/A' },
        { label: 'Heap', value: heapUsage, helper: heapHelper },
        { label: 'Latency', value: avgLatency !== null ? formatMilliseconds(avgLatency) : 'N/A' },
    ];
};

const buildCharts = (samples) => {
    const chartColor = 'var(--color-accent-indigo, #607bff)';
    const dangerColor = 'var(--color-status-down, #d02535)';
    const warningColor = 'var(--color-status-warning, #cebe1d)';

    const cpuData = samples.map((sample) => ({ timestamp: sample.timestamp, value: sample.cpuPercent })).filter(({ value }) => value !== null);
    const memoryData = samples.map((sample) => ({ timestamp: sample.timestamp, value: sample.memoryPercent })).filter(({ value }) => value !== null);
    const requestData = samples.map((sample) => ({ timestamp: sample.timestamp, value: sample.requestRatePerMinute })).filter(({ value }) => value !== null);
    const errorData = samples.map((sample) => ({ timestamp: sample.timestamp, value: sample.errorRatePerMinute })).filter(({ value }) => value !== null);
    const latencyData = samples.map((sample) => ({ timestamp: sample.timestamp, value: sample.latencyMs })).filter(({ value }) => value !== null);
    const loadData = samples.map((sample) => ({ timestamp: sample.timestamp, value: sample.load1m })).filter(({ value }) => value !== null);

    return [
        {
            key: 'cpu',
            title: 'CPU Usage (%)',
            data: cpuData,
            color: chartColor,
            valueFormatter: (value) => `${Math.round(value)}%`,
            yAxisFormatter: (value) => `${Math.round(value)}%`,
            emptyLabel: 'CPU metrics unavailable for this range.',
        },
        {
            key: 'memory',
            title: 'Heap Usage (%)',
            data: memoryData,
            color: warningColor,
            valueFormatter: (value) => `${Math.round(value)}%`,
            yAxisFormatter: (value) => `${Math.round(value)}%`,
            emptyLabel: 'Memory metrics unavailable for this range.',
        },
        {
            key: 'requests',
            title: 'Request Throughput (per minute)',
            data: requestData,
            color: chartColor,
            valueFormatter: (value) => formatInteger(value),
            yAxisFormatter: (value) => formatInteger(value),
            emptyLabel: 'Request throughput data unavailable for this range.',
        },
        {
            key: 'errors',
            title: 'Error Rate (per minute)',
            data: errorData,
            color: dangerColor,
            valueFormatter: (value) => formatInteger(value),
            yAxisFormatter: (value) => formatInteger(value),
            emptyLabel: 'Error rate data unavailable for this range.',
        },
        {
            key: 'latency',
            title: 'Average Latency (ms)',
            data: latencyData,
            color: chartColor,
            valueFormatter: (value) => formatMilliseconds(value),
            yAxisFormatter: (value) => `${formatInteger(value)} ms`,
            emptyLabel: 'Latency metrics unavailable for this range.',
        },
        {
            key: 'load',
            title: 'System Load (1m)',
            data: loadData,
            color: chartColor,
            valueFormatter: (value) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value),
            yAxisFormatter: (value) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value),
            emptyLabel: 'Load metrics unavailable for this range.',
        },
    ];
};

const EmptyHeader = () => (
    <div className="service-detail__header">
        <div className="service-detail__heading">
            <h2>Service insights</h2>
            <p>Select a service card to load Grafana-style monitoring with historical telemetry.</p>
        </div>
    </div>
);

export default ServiceDetailPanel;
